import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiError, api } from '../lib/api';
import type { AuthCaptchaConfig } from '../lib/types';
import { Turnstile } from '../components/Turnstile';
import { Button, Card, Field, Input } from '../components/ui';

/**
 * Where a provider button points.
 *
 * The API owns the redirect out; this only says where to come back to afterwards, and
 * only ever as a path. Anything else is discarded on the server too, but sending a whole
 * URL and relying on that would be building an open redirector and trusting somebody else
 * to refuse it.
 */
function ssoStartUrl(slug: string, state: unknown): string {
  const from = (state as { from?: string } | null)?.from;
  const safe = from && from.startsWith('/') && !from.startsWith('//') ? from : null;

  return safe
    ? `/v1/auth/sso/${encodeURIComponent(slug)}/start?returnPath=${encodeURIComponent(safe)}`
    : `/v1/auth/sso/${encodeURIComponent(slug)}/start`;
}

export function SignIn({ mode }: { mode: 'login' | 'register' }) {
  const { signedIn, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [connections, setConnections] =
    useState<{ slug: string; displayName: string; provider: string }[]>([]);

  const [captcha, setCaptcha] = useState<AuthCaptchaConfig | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Bumped after every attempt, because a Turnstile token dies with the request that spent
  // it. Also what makes the widget appear mid-session under the adaptive mode.
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [challenged, setChallenged] = useState(false);

  // Carried here by the reset page, so somebody arriving straight after changing their
  // password is told it worked rather than landing on an ordinary sign-in form and
  // wondering whether it did.
  const notice = (location.state as { notice?: string } | null)?.notice;

  const isRegister = mode === 'register';

  // Why a round trip through a provider ended back here. The API sends only a code, so the
  // wording is chosen on this side — and none of it distinguishes an address that has an
  // account from one that does not.
  const [params] = useSearchParams();
  const ssoFailure = params.get('sso');

  const ssoMessage = !ssoFailure ? null : ({
    cancelled: 'That sign-in was cancelled.',
    expired: 'That sign-in took too long, or had already been used. Try again.',
    sso_email_unverified:
      'Your provider has not confirmed that email address, so it cannot be used to sign in.',
    sso_domain_not_allowed:
      'That account cannot join this workspace on its own. Ask an administrator to invite you first.',
    no_membership: 'That account is not a member of any workspace yet.',
  } as Record<string, string>)[ssoFailure]
    ?? 'That sign-in could not be completed. Try again.';

  useEffect(() => {
    let cancelled = false;

    // A server with no provider configured answers mode "off", and this page then behaves
    // exactly as it did before there was a captcha at all. A failure to reach the endpoint
    // is treated the same way: sign-in is not blocked by a discovery call.
    api.auth.captcha()
      .then((config) => { if (!cancelled) setCaptcha(config); })
      .catch(() => { if (!cancelled) setCaptcha(null); });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // A server with none configured shows no buttons and this page is what it always was.
    // A failure to reach the endpoint is treated the same way: password sign-in must not
    // be blocked by a call that only decides whether to draw a button.
    api.auth.ssoConnections()
      .then((result) => { if (!cancelled) setConnections(result.data ?? []); })
      .catch(() => { if (!cancelled) setConnections([]); });

    return () => { cancelled = true; };
  }, []);

  if (signedIn) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const enabled = captcha !== null && captcha.mode !== 'off' && captcha.siteKey.length > 0;

  // Registration is challenged up front; sign-in is not, until the server says otherwise.
  // Showing a widget to everyone who types their password correctly the first time is a
  // tax on almost everybody to inconvenience almost nobody.
  const showCaptcha = enabled
    && (captcha.mode === 'always' || challenged || (isRegister && captcha.registerRequired));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (isRegister) {
        await register({
          email,
          password,
          organisationName: organisation || undefined,
          captchaToken: captchaToken ?? undefined,
        });
      } else {
        await login(email, password, captchaToken ?? undefined);
      }
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'captcha_required') {
        // Either the widget was not on screen yet, or the token it produced was refused.
        // Both mean the same thing to the person in front of it: solve this, then retry.
        setChallenged(true);
        setError(
          showCaptcha
            ? 'That verification could not be confirmed. Try the check again.'
            : 'Complete the verification check below, then sign in again.',
        );
      } else {
        setError(err instanceof ApiError
          ? err.message
          : 'Could not reach the server. Is the API running on port 5080?');
      }
    } finally {
      // Unconditionally, including after a success: a token is single use, and leaving a
      // spent one in state means the next attempt on this page is rejected for a reason
      // nothing on screen explains.
      setCaptchaToken(null);
      setCaptchaNonce((n) => n + 1);
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center p-6">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md
                           bg-[var(--ff-primary)] font-semibold text-[var(--ff-on-primary)]">
            F
          </span>
          <div>
            <h1 className="text-base font-semibold">
              {isRegister ? 'Create your workspace' : 'Sign in to FormForge'}
            </h1>
            <p className="text-xs text-[var(--ff-text-muted)]">
              {isRegister ? 'Free while you evaluate.' : 'Welcome back.'}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" noValidate>
          {notice && !error && (
            <p role="status" className="rounded-md border border-[var(--ff-border)]
                                        bg-[var(--ff-surface-raised)] px-3 py-2 text-sm">
              {notice}
            </p>
          )}

          {ssoMessage && !error && (
            <p role="alert" className="rounded-md border border-[var(--ff-danger)]
                                       bg-[var(--ff-danger-soft)] px-3 py-2 text-sm
                                       text-[var(--ff-danger)]">
              {ssoMessage}
            </p>
          )}

          {error && (
            <p role="alert" className="rounded-md border border-[var(--ff-danger)]
                                       bg-[var(--ff-danger-soft)] px-3 py-2 text-sm
                                       text-[var(--ff-danger)]">
              {error}
            </p>
          )}

          {isRegister && (
            <Field label="Organisation" hint="Used to name your workspace.">
              <Input
                value={organisation}
                onChange={(e) => setOrganisation(e.target.value)}
                placeholder="Acme Research"
                autoComplete="organization"
              />
            </Field>
          )}

          <Field label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>

          <Field
            label="Password"
            required
            // Length over composition rules, per NIST SP 800-63B (docs/10 §5).
            hint={isRegister ? 'At least 12 characters. Length matters more than symbols.' : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              minLength={isRegister ? 12 : undefined}
              required
            />
          </Field>

          {showCaptcha && captcha && (
            <div className="pt-1">
              <Turnstile
                siteKey={captcha.siteKey}
                onToken={setCaptchaToken}
                resetKey={captchaNonce}
              />
            </div>
          )}

          <Button type="submit" variant="primary" className="w-full" disabled={busy}>
            {busy ? 'Working…' : isRegister ? 'Create workspace' : 'Sign in'}
          </Button>

          {/* Under the button rather than beside the password field: somebody who knows
              their password should not be offered a way to change it on the way past. */}
          {!isRegister && (
            <p className="text-center text-xs">
              <Link className="text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)]"
                    to="/forgot-password">
                Forgotten your password?
              </Link>
            </p>
          )}
        </form>

        {connections.length > 0 && (
          <div className="mt-5">
            <div className="mb-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-[var(--ff-border)]" />
              <span className="text-xs text-[var(--ff-text-muted)]">or</span>
              <span className="h-px flex-1 bg-[var(--ff-border)]" />
            </div>

            <div className="space-y-2">
              {connections.map((connection) => (
                // A plain link, not a fetch. The provider needs the browser itself to
                // arrive, so it can recognise a session somebody already has there.
                <a
                  key={connection.slug}
                  href={ssoStartUrl(connection.slug, location.state)}
                  className="flex w-full items-center justify-center rounded-md border
                             border-[var(--ff-border)] px-3 py-2 text-sm
                             hover:bg-[var(--ff-surface-raised)]"
                >
                  Continue with {connection.displayName}
                </a>
              ))}
            </div>
          </div>
        )}

        <p className="mt-4 text-center text-xs text-[var(--ff-text-muted)]">
          {isRegister ? (
            <>Already have an account? <Link className="text-[var(--ff-primary)]" to="/login">Sign in</Link></>
          ) : (
            <>New here? <Link className="text-[var(--ff-primary)]" to="/register">Create a workspace</Link></>
          )}
        </p>
      </Card>
    </div>
  );
}
